import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
const FRONTMATTER_RE = /^\s*---\s*\n[\s\S]*?\n---\s*(?:\n|$)/;
const CRITICAL_BLOCK_RE = /<critical_system_prerequisite[^>]*>[\s\S]*?<\/critical_system_prerequisite>/gi;
function cleanChangeLogDocument(markdown) {
    return markdown
        .replace(/^\uFEFF/, '')
        .replace(FRONTMATTER_RE, '')
        .replace(CRITICAL_BLOCK_RE, '')
        .replace(/\n{3,}/g, '\n\n')
        .trim();
}
function isHttpUrl(value) {
    try {
        const url = new URL(value);
        return url.protocol === 'http:' || url.protocol === 'https:';
    }
    catch {
        return false;
    }
}
function resolveDocumentAssetUrl(url, baseUrl) {
    if (!url || !baseUrl || url.startsWith('http') || url.startsWith('/') || url.startsWith('#')) {
        return url;
    }
    try {
        return new URL(url, baseUrl).toString();
    }
    catch {
        return url;
    }
}
function createChangeLogDocumentComponents(baseUrl) {
    return {
        a: ({ href, children, node: _node, ...props }) => {
            const resolvedHref = resolveDocumentAssetUrl(href, baseUrl);
            return (_jsx("a", { href: resolvedHref, target: "_blank", rel: "noopener noreferrer", ...props, children: children }));
        },
        img: ({ src, alt, node: _node, ...props }) => {
            return _jsx("img", { src: resolveDocumentAssetUrl(src, baseUrl), alt: alt || '', ...props });
        },
    };
}
export function VersionChangeLogContent({ source, variant = 'inline', loadingText = '加载变更说明中...', errorText = '变更说明暂时无法加载', emptyText = '暂无变更说明', }) {
    const trimmedSource = source.trim();
    const isRemoteDocument = isHttpUrl(trimmedSource);
    const isFull = variant === 'full';
    const [markdown, setMarkdown] = useState('');
    const [loading, setLoading] = useState(isRemoteDocument);
    const [error, setError] = useState('');
    const components = useMemo(() => createChangeLogDocumentComponents(isRemoteDocument ? trimmedSource : undefined), [isRemoteDocument, trimmedSource]);
    useEffect(() => {
        if (!isRemoteDocument) {
            setMarkdown('');
            setLoading(false);
            setError('');
            return;
        }
        const controller = new AbortController();
        let active = true;
        setMarkdown('');
        setLoading(true);
        setError('');
        fetch(trimmedSource, { signal: controller.signal })
            .then((response) => {
            if (!response.ok) {
                throw new Error(`Change log request failed: ${response.status}`);
            }
            return response.text();
        })
            .then((text) => {
            if (!active)
                return;
            setMarkdown(cleanChangeLogDocument(text));
            setLoading(false);
        })
            .catch((fetchError) => {
            if (!active || fetchError.name === 'AbortError')
                return;
            console.error(fetchError);
            setError(errorText);
            setLoading(false);
        });
        return () => {
            active = false;
            controller.abort();
        };
    }, [isRemoteDocument, trimmedSource]);
    const renderState = (content) => {
        if (isFull) {
            return _jsx("div", { className: "skill-document-state", children: content });
        }
        return _jsx("div", { className: "skill-version-record__note skill-version-record__note--state", children: content });
    };
    const renderMarkdown = (content) => {
        const className = isFull
            ? 'skill-markdown'
            : 'skill-version-record__note skill-version-record__note--markdown skill-markdown';
        return (_jsx("div", { className: className, children: _jsx(ReactMarkdown, { remarkPlugins: [remarkGfm], components: components, children: content }) }));
    };
    if (!trimmedSource)
        return null;
    if (!isRemoteDocument) {
        if (isFull)
            return renderMarkdown(trimmedSource);
        return _jsx("p", { className: "skill-version-record__note", children: trimmedSource });
    }
    if (loading) {
        return renderState(loadingText);
    }
    if (error) {
        return renderState(_jsxs(_Fragment, { children: [error, _jsx("a", { href: trimmedSource, target: "_blank", rel: "noopener noreferrer", children: "\u67E5\u770B\u539F\u6587" })] }));
    }
    if (!markdown) {
        return renderState(emptyText);
    }
    return renderMarkdown(markdown);
}
